import { useTranslation } from '@/shared/contexts/TranslationContext';
import { useState, useRef, useEffect } from 'react';
import { FlagIcon } from '@/shared/components/ui/Icons';

type Locale = 'pt-BR' | 'en-US' | 'es-ES';

interface LanguageOption {
  code: Locale;
  name: string;
}

const languages: LanguageOption[] = [
  { code: 'pt-BR', name: 'Português' },
  { code: 'en-US', name: 'English' },
  { code: 'es-ES', name: 'Español' },
];

export function LanguageSelector() {
  const { locale, setLocale, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === locale) || languages[0];

  const handleToggleDropdown = () => setIsOpen((prev) => !prev);
  const handleCloseDropdown = () => setIsOpen(false);
  
  const handleLanguageSelect = (code: Locale) => {
    setLocale(code);
    handleCloseDropdown();
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleCloseDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Fechar com Esc
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseDropdown();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('language.select')}
        onClick={handleToggleDropdown}
        className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-600 transition"
      >
        <FlagIcon code={currentLanguage.code} className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <svg
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          id="language-selector-dropdown"
          role="menu"
          aria-label={t('language.select')}
          className="absolute right-0 z-50 mt-3 w-44 origin-top-right rounded-xl border border-gray-200 bg-white p-2 shadow-lg ring-1 ring-black/5 transition-transform duration-150"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              role="menuitem"
              onClick={() => handleLanguageSelect(lang.code)}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-gray-50 focus:bg-gray-50 ${
                locale === lang.code ? 'bg-green-50 text-green-700' : 'text-gray-700'
              }`}
            >
              <FlagIcon code={lang.code} className="h-6 w-6 flex-shrink-0" />
              <div className="flex-1 text-left">
                <div className="font-medium">{lang.name}</div>
                <div className="text-xs text-gray-400">{lang.code}</div>
              </div>

              {locale === lang.code ? (
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-white">
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
